import { useEffect, useState } from 'react';
import { ChevronLeft, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import { PRIVACY_URL, openExternal } from '../../app/links';
import { useSync, type SyncStatus } from '../../sync/SyncProvider';
import { Button, ButtonLink, Card, IconButton, List, ListRow, Modal, ScreenTitle, Section, TextField } from '../../ui';
import { usePremium } from '../../premium/PremiumProvider';
import './account.css';

// Supabase sends one e-mail per person per 60 s (Auth → SMTP → minimum interval).
const RESEND_AFTER_S = 60;

/** "just now", "3 min ago", "2 h ago" in the app language. */
export function useSince(at: number | null) {
  const { language } = useServices();
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  if (at === null) return null;
  const s = Math.round((Date.now() - at) / 1000);
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
  if (s < 45) return rtf.format(0, 'second');
  if (s < 3600) return rtf.format(-Math.round(s / 60), 'minute');
  if (s < 86_400) return rtf.format(-Math.round(s / 3600), 'hour');
  return rtf.format(-Math.round(s / 86_400), 'day');
}

export function useStatusText(status: SyncStatus, lastSyncedAt: number | null, pending = 0) {
  const { t } = useTranslation();
  const since = useSince(lastSyncedAt);
  switch (status) {
    case 'syncing':
      return t('account.status.syncing');
    case 'offline':
      return pending > 0 ? t('account.status.offlinePending', { count: pending }) : t('account.status.offline');
    case 'error':
      return pending > 0 ? t('account.status.errorPending', { count: pending }) : t('account.status.error');
    case 'idle':
      return since ? t('account.status.synced', { since }) : t('account.status.ready');
    default:
      return t('account.status.off');
  }
}

/**
 * Sign in with a code sent by e-mail: one method, the same account on
 * iPhone, Android and the web. The code field is the phone's one-time-code
 * field, so iOS and Android offer the code from Mail / Messages.
 */
export function SignInForm({ onSignedIn }: { onSignedIn?: (email: string) => void } = {}) {
  const { t } = useTranslation();
  const { sendCode, signIn } = useSync();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function send() {
    if (!validEmail || busy) {
      setError(validEmail ? null : t('account.emailInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await sendCode(email);
      setStep('code');
      setCode('');
      setWait(RESEND_AFTER_S);
    } catch (e) {
      console.warn('[account]', e);
      setError(t('account.sendFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!/^\d{6}$/.test(code.trim()) || busy) {
      setError(t('account.codeInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(email, code);
      setBusy(false);
      onSignedIn?.(email.trim());
    } catch (e) {
      console.warn('[account]', e);
      setError(t('account.codeRejected'));
      setBusy(false);
    }
  }

  if (step === 'email') {
    return (
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <TextField
          label={t('account.email')}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error ?? undefined}
        />
        <Button type="submit" variant="primary" size="lg" block loading={busy}>
          {t('account.sendCode')}
        </Button>
      </form>
    );
  }

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void verify();
      }}
    >
      <p className="account-note">{t('account.codeSent', { email: email.trim() })}</p>
      <TextField
        label={t('account.code')}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        error={error ?? undefined}
        hint={busy ? t('account.linking') : undefined}
      />
      <Button type="submit" variant="primary" size="lg" block loading={busy}>
        {t('account.signIn')}
      </Button>
      <div className="account-links">
        <button type="button" className="link-button" disabled={wait > 0 || busy} onClick={() => void send()}>
          {wait > 0 ? t('account.resendIn', { s: wait }) : t('account.resend')}
        </button>
        <button
          type="button"
          className="link-button"
          disabled={busy}
          onClick={() => {
            setStep('email');
            setError(null);
          }}
        >
          {t('account.otherEmail')}
        </button>
      </div>
    </form>
  );
}

/** More → Account & sync. */
export function AccountScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { available, account, status, lastSyncedAt, pending, held, syncNow, signOut, deleteAccount } = useSync();
  // Changes held for Premium are not "waiting to sync": they are said on their own below.
  const statusText = useStatusText(status, lastSyncedAt, pending - held);
  const premium = usePremium();
  const syncLocked = premium.ready && !premium.has('sync');
  const [deleting, setDeleting] = useState<'ask' | 'busy' | 'failed' | null>(null);

  async function confirmDelete() {
    setDeleting('busy');
    try {
      // On success the app starts over; there is nothing left here to update.
      await deleteAccount();
    } catch (e) {
      console.error('[account] delete', e);
      setDeleting('failed');
    }
  }

  return (
    <>
      <div className="screen-back">
        <IconButton icon={ChevronLeft} label={t('catalog.backToMore')} onClick={() => navigate('/more')} />
      </div>
      <ScreenTitle>{t('account.title')}</ScreenTitle>

      {!available ? (
        <Card>
          <p className="account-note">{t('account.unavailable')}</p>
        </Card>
      ) : account ? (
        <>
          <Card flush>
            <List inset>
              <ListRow title={t('account.signedInAs')} subtitle={account.email} />
              <ListRow
                title={t('account.statusLabel')}
                subtitle={
                  <span className={status === 'error' ? 'tone-up' : undefined} data-testid="sync-status">
                    {statusText}
                  </span>
                }
              />
            </List>
          </Card>
          {syncLocked && (
            <div className="screen-actions stack stack-tight" data-testid="sync-held">
              <p className="account-note">
                {t('premium.syncHeld')}
                {held > 0 && ` ${t('premium.syncHeldCount', { count: held })}`}
              </p>
              <ButtonLink to="/premium?for=sync" variant="primary" size="lg" block>
                {t('premium.see')}
              </ButtonLink>
            </div>
          )}
          <div className="screen-actions stack">
            <Button variant="secondary" size="lg" block icon={RefreshCw} loading={status === 'syncing'} onClick={() => void syncNow()}>
              {t('account.syncNow')}
            </Button>
            <Button variant="ghost" size="lg" block onClick={() => void signOut()}>
              {t('account.signOut')}
            </Button>
            <p className="account-note">{t('account.signOutNote')}</p>
          </div>
          <div className="screen-actions stack">
            <Button variant="ghost" size="lg" block className="tone-up" onClick={() => setDeleting('ask')}>
              {t('account.deleteAccount')}
            </Button>
          </div>
          <Modal
            open={deleting !== null}
            onClose={() => deleting !== 'busy' && setDeleting(null)}
            title={t('account.deleteTitle')}
            footer={
              <>
                <Button variant="danger" loading={deleting === 'busy'} onClick={() => void confirmDelete()} data-testid="account-delete-confirm">
                  {t('account.deleteConfirm')}
                </Button>
                <Button disabled={deleting === 'busy'} onClick={() => setDeleting(null)}>
                  {t('account.cancel')}
                </Button>
              </>
            }
          >
            <div className="stack stack-tight">
              <p>{t('account.deleteBody')}</p>
              <p className="account-note">{t('account.deleteKeep')}</p>
              {deleting === 'failed' && (
                <p className="field-msg field-error" role="alert">
                  {t('account.deleteFailed')}
                </p>
              )}
            </div>
          </Modal>
        </>
      ) : (
        <Section divided={false}>
          <p className="account-intro">{t('account.intro')}</p>
          {syncLocked && <p className="account-note">{t('premium.signInFree')}</p>}
          <SignInForm />
          <p className="account-note">{t('account.privacy')}</p>
          <button type="button" className="link-button" onClick={() => openExternal(PRIVACY_URL)}>
            {t('more.privacy')}
          </button>
        </Section>
      )}
    </>
  );
}
